import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Stack,
  Chip,
  IconButton,
  Collapse,
  useTheme,
  alpha,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import {
  Folder as FolderIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Done as DoneIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import SourceDataTable from "../../utils/SourceDataTable";
import DialogLoading from "../../App/DialogLoading";
import FullscreenButton from "../../utils/button/fullscreenButton";
import FullscreenDialog from "../../utils/dialog/FullscreenDialog";
import { CustomDialogBase } from "../../utils/dialog/customDialog";
import {
  CameraExportButton,
  exportComponentAsImage,
} from "../../../utils/export/componentExporter";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { StyledIconButton } from "../../utils/Icon";

// Opening the dialog should not re-render the schedule behind it.
const ScheduleFullscreen = ({ title, subtitle, children }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <FullscreenButton handleDialogOpen={() => setOpen(true)} />
      <FullscreenDialog
        open={open}
        onClose={() => setOpen(false)}
        title={title}
        subtitle={subtitle}
      >
        {children}
      </FullscreenDialog>
    </>
  );
};

const OverflowTooltip = ({ title, children, sx, ...tooltipProps }) => {
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element) return undefined;

    const updateOverflow = () => {
      setIsOverflowing(
        element.scrollWidth > element.clientWidth ||
          element.scrollHeight > element.clientHeight
      );
    };

    updateOverflow();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateOverflow);
      observer.observe(element);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateOverflow);
    return () => window.removeEventListener("resize", updateOverflow);
  }, [title, children]);

  return (
    <Tooltip title={isOverflowing ? title : ""} {...tooltipProps}>
      <Box component='span' ref={textRef} sx={sx}>
        {children}
      </Box>
    </Tooltip>
  );
};

const normalizeSectionOption = (option, fallbackLabel) => {
  if (Array.isArray(option)) {
    return {
      label: option[0],
      value: option[1] ?? option[0],
    };
  }

  if (option && typeof option === "object") {
    const value = option.value ?? option.section ?? option.key ?? fallbackLabel;
    const label = option.label ?? option.name ?? fallbackLabel ?? value;

    return { label, value };
  }

  return {
    label: fallbackLabel ?? option,
    value: option,
  };
};

const normalizeSectionGroups = (options) => {
  if (Array.isArray(options)) {
    const hasGroups = options.some(
      (option) => option && typeof option === "object" && option.options
    );

    if (hasGroups) {
      return options
        .map((group) => {
          const groupOptions = Array.isArray(group.options)
            ? group.options.map((option) => normalizeSectionOption(option))
            : Object.entries(group.options || {}).map(([label, value]) =>
                normalizeSectionOption(value, label)
              );

          return {
            label: group.label ?? group.group ?? group.title ?? "",
            options: groupOptions,
          };
        })
        .filter((group) => group.options.length > 0);
    }

    return [
      {
        label: "",
        options: options.map((option) => normalizeSectionOption(option)),
      },
    ];
  }

  return [
    {
      label: "",
      options: Object.entries(options || {}).map(([label, value]) =>
        normalizeSectionOption(value, label)
      ),
    },
  ];
};

const ScheduleContainer = ({
  id,
  schedule = {},
  schName = "",
  children,
  icon: Icon,
  loading = false,
  updateHeading,
  taxAdjustment = "",
  updateAdjustment,
  section = "",
  updateSection,
  allowedToEditSchName = false,
  allowedToAdjustTax = false,
  allowedToEditSection = false,
  taxAdjustOptions = ["", "Added Back", "Deduct"],
  sectionOptions = {},
  collapsible = false,
  defaultExpanded = true,
  fullScreenMode = true,
  isSourceApplicable = false,
  sourceData = [],
  onImport,
  howItWorksTitle = "How it works",
  howItWorksContent = null,
  onHowItWorksClick,
  canEditSchedule = true,
}) => {
  const theme = useTheme();

  const exportRef = useRef(null);
  const statusResetTimers = useRef({});
  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, schName);
    }
  };

  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState("");
  const [titleError, setTitleError] = useState("");
  const [tempTaxAdjustment, setTempTaxAdjustment] = useState("");
  const [tempSection, setTempSection] = useState("");
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [sectionPopoverAnchor, setSectionPopoverAnchor] = useState(null);
  const [saveStatus, setSaveStatus] = useState({
    title: "idle",
    taxAdjustment: "idle",
    section: "idle",
  });

  useEffect(() => {
    if (!isEditingTitle) setTempTitle(schName || schedule.name || "");
  }, [schName, schedule.name, isEditingTitle]);

  useEffect(() => {
    setTempTaxAdjustment(taxAdjustment);
  }, [taxAdjustment]);

  useEffect(() => {
    setTempSection(section);
  }, [section]);

  useEffect(() => {
    const timers = statusResetTimers.current;

    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const canEditTitle =
    canEditSchedule &&
    allowedToEditSchName &&
    typeof updateHeading === "function";
  const isApplyingTitle = saveStatus.title === "saving";

  const handleTitleEdit = () => {
    if (!canEditTitle) return;
    clearTimeout(statusResetTimers.current.title);
    setTempTitle(schName || schedule.name || "");
    setTitleError("");
    updateFieldStatus("title", "idle");
    setIsEditingTitle(true);
  };

  const handleApplyTitle = async (event) => {
    event.preventDefault();
    if (!canEditTitle || isApplyingTitle) return;
    const nextTitle = tempTitle.trim();
    if (!nextTitle) {
      setTitleError("Enter a schedule title.");
      return;
    }
    if (nextTitle === (schName || schedule.name || "")) {
      setIsEditingTitle(false);
      return;
    }

    updateFieldStatus("title", "saving");
    setTitleError("");
    try {
      await updateHeading(nextTitle);
      setIsEditingTitle(false);
      updateFieldStatus("title", "updated");
      resetFieldStatusSoon("title");
    } catch (error) {
      updateFieldStatus("title", "error");
      setTitleError("Couldn't update the title. Please try again.");
    }
  };

  const handleCancelEdit = () => {
    if (isApplyingTitle) return;
    setTempTitle(schName || schedule.name || "");
    setTitleError("");
    updateFieldStatus("title", "idle");
    setIsEditingTitle(false);
  };

  const updateFieldStatus = (field, status) => {
    setSaveStatus((prev) => ({ ...prev, [field]: status }));
  };

  const resetFieldStatusSoon = (field) => {
    clearTimeout(statusResetTimers.current[field]);
    statusResetTimers.current[field] = setTimeout(() => {
      updateFieldStatus(field, "idle");
    }, 1800);
  };

  const commitInlineChange = async ({
    field,
    previousValue,
    nextValue,
    setValue,
    updateFn,
  }) => {
    if (nextValue === null || nextValue === previousValue || !updateFn) return;

    setValue(nextValue);
    updateFieldStatus(field, "saving");

    try {
      await Promise.resolve(updateFn(nextValue));
      updateFieldStatus(field, "saved");
    } catch (error) {
      setValue(previousValue);
      updateFieldStatus(field, "error");
    } finally {
      resetFieldStatusSoon(field);
    }
  };

  const getTaxAdjustmentColor = (adjustment) => {
    switch (adjustment) {
      case "Added Back":
        return "success";
      case "Deduct":
        return "error";
      case "Unremitted Foreign Income":
        return "warning";
      default:
        return "default";
    }
  };

  const getTaxAdjustmentSelectedStyles = (adjustment) => {
    const colorMap = {
      "Added Back": theme.palette.success.main,
      Deduct: theme.palette.error.main,
      "": theme.palette.grey[600],
    };
    const selectedColor = colorMap[adjustment] || theme.palette.primary.main;

    return {
      "&.Mui-selected": {
        bgcolor: alpha(selectedColor, 0.1),
        color: selectedColor,
        borderColor: alpha(selectedColor, 0.6),
        "&:hover": {
          bgcolor: alpha(selectedColor, 0.16),
        },
      },
    };
  };

  const getSectionColor = (sectionValue) => {
    // You can customize colors based on section types
    switch (sectionValue) {
      case "Tax Adjustments":
      case "Tax adjustment (separate sourced income)":
      case "Tax adjustment (balancing charge)":
        return "primary";
      case "Further Deductions":
      case "Further deduction":
        return "secondary";
      case "Current Year Capital Allowances":
        return "info";
      case "Unutilised Capital Allowances":
        return "warning";
      case "Unutilised Losses":
        return "error";
      case "Unutilised Donations":
        return "success";
      case "Other Source Income":
        return "success";
      case "Current Year Donations":
        return "success";
      case "Partial Tax Exemption / Full Tax Exemption":
        return "warning";
      case "Corporate Income Tax Rebate":
        return "primary";
      case "Tax Previously Assessed":
        return "secondary";
      default:
        return "default";
    }
  };

  const getTaxAdjustmentLabel = (value) =>
    value === "" ? "No Adjustment" : value;

  const getSectionLabel = (value) => (value === "" ? "No Section" : value);

  const getReadOnlyTooltip = (value, disabledReason) =>
    disabledReason ? `${value} - ${disabledReason}` : value;

  const getSectionAccentColor = (sectionValue) => {
    const color = getSectionColor(sectionValue);

    return theme.palette[color]?.main || theme.palette.grey[600];
  };

  const getFieldStatusStyles = (status) => {
    return {};
  };

  const getReadonlyReason = (allowedToEdit) => {
    if (!canEditSchedule) return "Schedule is read-only";
    if (!allowedToEdit) return "No edit permission";
    return "";
  };

  const canEditTaxAdjustment =
    canEditSchedule && allowedToAdjustTax && Boolean(updateAdjustment);
  const canEditSection =
    canEditSchedule && allowedToEditSection && Boolean(updateSection);
  const showSectionControl =
    section !== undefined && (allowedToEditSection || section !== "");
  const sectionOptionGroups = normalizeSectionGroups(sectionOptions);
  const sectionOptionList = sectionOptionGroups.flatMap(
    (group) => group.options
  );
  const getSectionOptionLabel = (value) =>
    sectionOptionList.find((option) => option.value === value)?.label ||
    getSectionLabel(value);

  const renderTaxAdjustmentControl = () => {
    const status = saveStatus.taxAdjustment;

    if (!canEditTaxAdjustment) {
      return (
        <Tooltip
          title={getReadOnlyTooltip(
            getTaxAdjustmentLabel(taxAdjustment),
            getReadonlyReason(allowedToAdjustTax)
          )}
        >
          <Chip
            label={getTaxAdjustmentLabel(taxAdjustment)}
            color={getTaxAdjustmentColor(taxAdjustment)}
            size='small'
            variant='outlined'
            sx={{
              fontWeight: 600,
              borderRadius: 2,
              maxWidth: 180,
              opacity: canEditSchedule ? 1 : 0.72,
              transition: "opacity 0.2s ease, border-color 0.2s ease",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
        </Tooltip>
      );
    }

    if (taxAdjustOptions.length > 4) {
      return (
        <Stack direction='row' spacing={0.75} alignItems='center'>
          <FormControl
            size='small'
            sx={{
              minWidth: 160,
              maxWidth: 220,
              transition: "box-shadow 0.2s ease, background-color 0.2s ease",
              borderRadius: 2,
              ...getFieldStatusStyles(status),
            }}
          >
            <InputLabel>Tax Adjustment</InputLabel>
            <Select
              value={tempTaxAdjustment}
              onChange={(e) =>
                commitInlineChange({
                  field: "taxAdjustment",
                  label: "Tax adjustment",
                  previousValue: tempTaxAdjustment,
                  nextValue: e.target.value,
                  setValue: setTempTaxAdjustment,
                  updateFn: updateAdjustment,
                })
              }
              label='Tax Adjustment'
              disabled={status === "saving"}
              renderValue={(value) => getTaxAdjustmentLabel(value)}
              sx={{
                borderRadius: 2,
                "& .MuiSelect-select": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            >
              {taxAdjustOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {getTaxAdjustmentLabel(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      );
    }

    return (
      <Stack direction='row' spacing={0.75} alignItems='center'>
        <ToggleButtonGroup
          exclusive
          size='small'
          value={tempTaxAdjustment}
          disabled={status === "saving"}
          onChange={(event, nextValue) =>
            commitInlineChange({
              field: "taxAdjustment",
              label: "Tax adjustment",
              previousValue: tempTaxAdjustment,
              nextValue,
              setValue: setTempTaxAdjustment,
              updateFn: updateAdjustment,
            })
          }
          sx={{
            flexWrap: "wrap",
            borderRadius: 2,
            transition: "box-shadow 0.2s ease, background-color 0.2s ease",
            ...getFieldStatusStyles(status),
            "& .MuiToggleButton-root": {
              minHeight: 30,
              maxWidth: 116,
              px: 1.25,
              py: 0.25,
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 600,
            },
          }}
        >
          {taxAdjustOptions.map((option) => (
            <ToggleButton
              key={option}
              value={option}
              sx={getTaxAdjustmentSelectedStyles(option)}
            >
              <OverflowTooltip
                title={getTaxAdjustmentLabel(option)}
                sx={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getTaxAdjustmentLabel(option)}
              </OverflowTooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
    );
  };

  const renderSectionControl = () => {
    const status = saveStatus.section;
    const sectionPopoverOpen = Boolean(sectionPopoverAnchor);
    const selectedSectionLabel = getSectionOptionLabel(tempSection);
    const selectedSectionAccent = getSectionAccentColor(tempSection);

    const handleSectionPopoverOpen = (event) => {
      if (status === "saving") return;
      setSectionPopoverAnchor(event.currentTarget);
    };

    const handleSectionPopoverClose = () => {
      setSectionPopoverAnchor(null);
    };

    const handleSectionSelect = (nextValue) => {
      handleSectionPopoverClose();
      commitInlineChange({
        field: "section",
        previousValue: tempSection,
        nextValue,
        setValue: setTempSection,
        updateFn: updateSection,
      });
    };

    if (!canEditSection) {
      return (
        <Chip
          label={getSectionOptionLabel(section)}
          color={getSectionColor(section)}
          size='small'
          variant='outlined'
          sx={{
            fontWeight: 600,
            borderRadius: 2,
            maxWidth: 220,
            opacity: canEditSchedule ? 1 : 0.72,
            transition: "opacity 0.2s ease, border-color 0.2s ease",
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        />
      );
    }

    return (
      <Stack direction='row' spacing={0.75} alignItems='center'>
        <Box
          component='button'
          type='button'
          onClick={handleSectionPopoverOpen}
          aria-disabled={status === "saving"}
          aria-haspopup='listbox'
          aria-expanded={sectionPopoverOpen}
          sx={{
            minHeight: 32,
            width: "100%",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: sectionPopoverOpen
              ? alpha(selectedSectionAccent, 0.8)
              : alpha(theme.palette.divider, 0.3),
            bgcolor: sectionPopoverOpen
              ? alpha(selectedSectionAccent, 0.08)
              : alpha(theme.palette.background.paper, 0.1),
            color: theme.palette.text.primary,
            cursor: status === "saving" ? "default" : "pointer",
            boxShadow: sectionPopoverOpen
              ? `0 0 0 3px ${alpha(selectedSectionAccent, 0.1)}`
              : "none",
            transition:
              "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
            ...getFieldStatusStyles(status),
            "&:hover": {
              borderColor: alpha(selectedSectionAccent, 0.5),
              bgcolor: alpha(selectedSectionAccent, 0.06),
            },
            '&[aria-disabled="true"]': {
              opacity: 0.64,
            },
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: selectedSectionAccent,
              flexShrink: 0,
            }}
          />
          <Typography
            variant='caption'
            fontWeight={700}
            sx={{
              maxWidth: 190,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.4,
            }}
          >
            {selectedSectionLabel}
          </Typography>
          <ExpandMoreIcon
            sx={{
              marginLeft: "auto",
              fontSize: 17,
              color: theme.palette.text.secondary,
              transform: sectionPopoverOpen ? "rotate(180deg)" : "none",
              transition: "transform 0.2s ease",
            }}
          />
        </Box>

        <Popover
          open={sectionPopoverOpen}
          anchorEl={sectionPopoverAnchor}
          onClose={handleSectionPopoverClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 1,
              width: 340,
              maxWidth: "calc(100vw - 32px)",
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.98),
              boxShadow: `0 18px 48px ${alpha(
                theme.palette.common.black,
                0.18
              )}`,
              overflow: "hidden",
            },
          }}
        >
          <Box sx={{ px: 1.5, pt: 1.25, pb: 1 }}>
            <Typography
              variant='overline'
              color='text.secondary'
              sx={{ fontWeight: 700, lineHeight: 1.2 }}
            >
              Adjust to Main Comp Section
            </Typography>
          </Box>
          <Box
            role='listbox'
            sx={{
              maxHeight: 320,
              overflowY: "auto",
              px: 0.75,
              pb: 0.75,
            }}
          >
            {sectionOptionGroups.map((group, groupIndex) => (
              <Box key={group.label || groupIndex} sx={{ mb: 0.75 }}>
                {group.label && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{
                      display: "block",
                      px: 1,
                      pt: groupIndex === 0 ? 0 : 0.75,
                      pb: 0.5,
                      fontSize: "0.6875rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    {group.label}
                  </Typography>
                )}
                {group.options.map((option) => {
                  const isSelected = option.value === tempSection;
                  const accentColor = getSectionAccentColor(option.value);

                  return (
                    <Tooltip
                      key={`${group.label}-${option.value}`}
                      title={option.label}
                      placement='left'
                    >
                      <Box
                        component='button'
                        type='button'
                        role='option'
                        aria-selected={isSelected}
                        onClick={() => handleSectionSelect(option.value)}
                        sx={{
                          width: "100%",
                          minHeight: 38,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1,
                          py: 0.75,
                          mb: 0.25,
                          border: 0,
                          borderRadius: 1.25,
                          bgcolor: isSelected
                            ? alpha(accentColor, 0.1)
                            : "transparent",
                          color: theme.palette.text.primary,
                          textAlign: "left",
                          cursor: "pointer",
                          transition:
                            "background-color 0.16s ease, color 0.16s ease",
                          "&:hover": {
                            bgcolor: isSelected
                              ? alpha(accentColor, 0.14)
                              : alpha(theme.palette.action.hover, 0.12),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: accentColor,
                            opacity: isSelected ? 1 : 0.7,
                            flexShrink: 0,
                          }}
                        />
                        <Box
                          component='span'
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "0.8125rem",
                            fontWeight: isSelected ? 700 : 500,
                          }}
                        >
                          {option.label}
                        </Box>
                        {isSelected && (
                          <CheckIcon
                            sx={{
                              fontSize: 17,
                              color: accentColor,
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Popover>
      </Stack>
    );
  };
  const renderContent = (isFullscreen = false) => {
    return (
      <>
        <Box>
          {/* Content Area */}
          <Box sx={{ minHeight: 100 }}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 8,
                }}
              >
                <DialogLoading title='Preparing the schedule...' />
              </Box>
            ) : (
              children(isFullscreen)
            )}
          </Box>

          {isSourceApplicable && sourceData && (
            <SourceDataTable
              data={sourceData}
              title='Tax Adjustment References'
              defaultExpanded={false}
              onImport={onImport}
              compact
              canEdit={canEditSchedule}
            />
          )}
        </Box>
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: alpha(theme.palette.grey[50], 0.5),
            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
          }}
        >
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography variant='body2' color='text.secondary'>
              {schedule.type} Schedule
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Sch {schedule.scheduleNo}
            </Typography>
          </Stack>
        </Box>
      </>
    );
  };

  return (
    <Paper
      ref={exportRef}
      id={id}
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 4,
        border: "1px solid",
        borderColor: theme.palette.divider,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.3),
          boxShadow: `0px 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          transform: "translateY(-2px)",
        },
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        spacing={2}
        sx={{
          px: 3,
          py: 2.5,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.06
          )}, ${alpha(theme.palette.primary.main, 0.02)})`,
          borderBottom: "1px solid",
          borderColor: alpha(theme.palette.primary.main, 0.1),
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.1
            )}, ${alpha(theme.palette.primary.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          {Icon ? (
            <Icon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
          ) : (
            <FolderIcon
              sx={{ color: theme.palette.primary.main, fontSize: 24 }}
            />
          )}
        </Box>

        <Box flex={1} sx={{ minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            {isEditingTitle ? (
              <Box
                component='form'
                aria-label='Edit schedule title'
                onSubmit={handleApplyTitle}
                onKeyDown={(event) => {
                  if (event.nativeEvent.isComposing || event.keyCode === 229) {
                    return;
                  }
                  if (event.key === "Escape") {
                    event.preventDefault();
                    event.stopPropagation();
                    handleCancelEdit();
                  }
                }}
                sx={{
                  maxWidth: 560,
                  p: 1.25,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.25),
                  bgcolor: "background.paper",
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.05)}`,
                }}
              >
                <Typography
                  variant='caption'
                  color='text.secondary'
                  fontWeight={600}
                  sx={{ display: "block", mb: 0.75 }}
                >
                  Schedule title
                </Typography>
                <Stack
                  direction='row'
                  alignItems='flex-start'
                  useFlexGap
                  flexWrap='wrap'
                  gap={0.75}
                >
                  <TextField
                    autoFocus
                    fullWidth
                    value={tempTitle}
                    onChange={(event) => {
                      setTempTitle(event.target.value);
                      setTitleError("");
                    }}
                    onFocus={(event) => event.target.select()}
                    onKeyDown={(event) => {
                      if (
                        event.nativeEvent.isComposing ||
                        event.keyCode === 229
                      ) {
                        if (event.key === "Enter") event.preventDefault();
                      }
                    }}
                    inputProps={{ "aria-label": "Schedule title" }}
                    disabled={isApplyingTitle}
                    error={Boolean(titleError)}
                    helperText={titleError || undefined}
                    size='small'
                    sx={{
                      flex: "1 1 200px",
                      minWidth: 0,
                      "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                      "& .MuiInputBase-input": {
                        fontSize: "0.875rem",
                        fontWeight: 600,
                      },
                      "& .MuiFormHelperText-root": { mx: 0 },
                    }}
                  />
                  <Button
                    type='submit'
                    variant='contained'
                    size='small'
                    disableElevation
                    disabled={isApplyingTitle || !canEditTitle}
                    startIcon={
                      isApplyingTitle ? (
                        <CircularProgress size={14} color='inherit' />
                      ) : (
                        <DoneIcon fontSize='small' />
                      )
                    }
                    sx={{
                      minHeight: 40,
                      borderRadius: 1.5,
                      textTransform: "none",
                    }}
                  >
                    {isApplyingTitle ? "Applying" : "Apply"}
                  </Button>
                  <Button
                    type='button'
                    size='small'
                    color='inherit'
                    onClick={handleCancelEdit}
                    disabled={isApplyingTitle}
                    startIcon={<CloseIcon fontSize='small' />}
                    sx={{
                      minHeight: 40,
                      borderRadius: 1.5,
                      color: "text.secondary",
                      textTransform: "none",
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: "block", mt: 0.75 }}
                >
                  Enter to apply · Esc to cancel
                </Typography>
              </Box>
            ) : (
              <Stack
                direction='row'
                alignItems='center'
                useFlexGap
                flexWrap='wrap'
                gap={1}
                sx={{ minWidth: 0 }}
              >
                <Typography
                  variant='body1'
                  fontWeight={600}
                  sx={{
                    minWidth: 0,
                    lineHeight: 1.5,
                    overflowWrap: "anywhere",
                  }}
                >
                  {schName || schedule.name || "Untitled Schedule"}
                </Typography>
                {canEditTitle && (
                  <Tooltip title='Rename this schedule'>
                    <Button
                      onClick={handleTitleEdit}
                      size='small'
                      aria-label='Rename this schedule'
                      sx={{
                        flexShrink: 0,
                        minWidth: 28,
                        minHeight: 28,
                        px: 0.75,
                        py: 0.5,
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "transparent",
                        bgcolor: "transparent",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        textTransform: "none",
                        transition:
                          "color 0.18s ease, background-color 0.18s ease, border-color 0.18s ease",
                        "& .rename-label": {
                          maxWidth: 0,
                          ml: 0,
                          opacity: 0,
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          transition:
                            "max-width 0.18s ease, margin-left 0.18s ease, opacity 0.18s ease",
                        },
                        "&:hover, &.Mui-focusVisible": {
                          color: "primary.main",
                          borderColor: alpha(theme.palette.primary.main, 0.25),
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          "& .rename-label": {
                            maxWidth: 64,
                            ml: 0.75,
                            opacity: 1,
                          },
                        },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16, flexShrink: 0 }} />
                      <Box
                        component='span'
                        className='rename-label'
                        aria-hidden='true'
                      >
                        Rename
                      </Box>
                    </Button>
                  </Tooltip>
                )}
                {saveStatus.title === "updated" && (
                  <Stack
                    direction='row'
                    alignItems='center'
                    gap={0.5}
                    role='status'
                    sx={{ color: "success.main" }}
                  >
                    <CheckIcon sx={{ fontSize: 16 }} />
                    <Typography variant='caption' fontWeight={600}>
                      Remember to save the schedule to apply the new title.
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}
          </Box>
          {schedule.scheduleNo && (
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              Schedule No: {schedule.scheduleNo}
            </Typography>
          )}
          {schedule.type && (
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              {schedule.type} Schedule{" "}
              {(howItWorksContent || onHowItWorksClick) && (
                <Box
                  component='span'
                  className='no-export'
                  sx={{ display: "inline-block", ml: 0.75, verticalAlign: "middle" }}
                >
                  <StyledIconButton
                    onClick={onHowItWorksClick || (() => setHowItWorksOpen(true))}
                    tooltip='How it works'
                    icon={HelpOutlineIcon}
                    iconSize={19}
                    sx={{
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                      border: "1px solid",
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                      borderRadius: 1.5,
                      height: 32,
                      width: 32,
                      p: 0,
                      transition: "background-color 0.2s ease, border-color 0.2s ease",
                      "&:hover": {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        borderColor: "primary.main",
                      },
                    }}
                  />
                </Box>
              )}
            </Typography>
          )}
        </Box>

        <Stack direction='row' spacing={1.5} alignItems='center'>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* Tax Adjustment Control */}
            {taxAdjustment !== undefined && renderTaxAdjustmentControl()}
            {showSectionControl && <Box sx={{ height: 8 }} />}
            {showSectionControl && renderSectionControl()}
          </div>
          <CameraExportButton
            onClick={handleExportAsImage}
            tooltipTitle='Screenshot of the schedule'
            sx={{ ml: 1, mr: -0.5 }}
          />

          {fullScreenMode && (
            <ScheduleFullscreen
              title={schName || schedule.name || "Untitled Schedule"}
              subtitle={`Schedule No: ${schedule.scheduleNo} (${schedule.type} Schedule)`}
            >
              {() => renderContent(true)}
            </ScheduleFullscreen>
          )}

          {collapsible && (
            <IconButton
              onClick={() => setExpanded(!expanded)}
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease-in-out",
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          )}
        </Stack>
      </Stack>

      <Collapse in={!collapsible || expanded}>{renderContent()}</Collapse>

      {howItWorksContent && (
        <CustomDialogBase
          open={howItWorksOpen}
          onClose={() => setHowItWorksOpen(false)}
          title={howItWorksTitle}
          type='info'
        >
          {howItWorksContent}
        </CustomDialogBase>
      )}
    </Paper>
  );
};

export default ScheduleContainer;
